/**
 * AppToolbar — the ONE shared form action bar (CLAUDE.md §18.6).  Every form's
 * toolbar renders through here so the buttons appear in the SAME canonical order
 * with the SAME theme-orange style on every form.  Forms only choose which
 * buttons show / are enabled (via props) and supply any form-specific buttons
 * (the `left` slot) — they never reorder or restyle.
 *
 * CANONICAL ORDER (left → right):
 *   [ form-specific buttons (left slot) ] · New · Edit · Delete ·
 *   [ `beforeRefresh` slot ] · Refresh · [Import · Template] · Export to
 *
 * THREE opt-in exceptions, for forms whose LEGACY ALPACA bar puts a standard
 * action AHEAD of its form-specific ones.  Each is a PROP on this shared
 * component, never a form reordering or hand-rolling its own buttons (that
 * would lose the auto-registered hotkey and the shared glyph/style), and each
 * changes ONLY that button's position — every form that passes neither keeps
 * the canonical order above:
 *   · `newFirst`  (2026-09-24, owner request) renders New BEFORE the `left`
 *     slot.  Used by Item Requisition, whose legacy bar is
 *     New · Submit · History · Refresh · Export to.
 *   · `editFirst` (2026-09-29, owner request) renders Edit BEFORE the `left`
 *     slot.  Used by Vendors COA, whose legacy bar is
 *     Edit · History · Refresh · Export to — verified against the live GoLive
 *     VendorReagentCertificate_ListView, which draws Edit first.
 *   · `crudFirst` (2026-09-29, owner request) renders New · Edit · Delete —
 *     the whole CRUD group, in that order — BEFORE the `left` slot.  Used by
 *     Item Initialization, whose legacy bar is
 *     New · Edit · Delete · Import · Download Template · Refresh · Export to ·
 *     Audit Trail · Search.  It is the group form of `newFirst`: those two move
 *     ONE button, and a form whose legacy bar hoists all three would otherwise
 *     have to hand-roll them into `left` and lose the shared glyphs and the
 *     auto-registered Alt+N / Alt+E / Alt+D.  `crudFirst` implies newFirst and
 *     editFirst, so passing it with either is redundant, not contradictory.
 *   · `refreshFirst` (2026-10-14, owner request) renders Refresh at the very
 *     FRONT of the bar.  Used by Contract Lab Test Pricing, whose legacy bar is
 *     Refresh · Add · Remove · Save · Export to · Search (with `crudFirst`,
 *     `newLabel="Add"` and `deleteLabel="Remove"`).
 *   · `previewLast` (2026-10-16, owner request) renders Preview (with its
 *     `beforePreview` slot) AFTER Export to.  Used by Data Center › PLM
 *     Contamination Check, whose prototype bar is Refresh · Export to · Preview.
 *
 * The reload action is always labelled **Refresh** (never "Retrieve").
 * All buttons use the shared `.ca-btn` (theme-orange) style.  Layout (compact
 * uniform spacing, one control height, group dividers) lives in the co-located
 * AppToolbar.css so it applies on every form automatically.
 *
 * NOTE (BO UI Standard v2 ruling, 2026-07-01): Audit Trail is VIEW-ONLY and is
 * NOT a toolbar action — it renders as a quiet chip on the TITLE ROW via
 * `<FormTitle auditFormName=… auditSourceId=…>`, never here.
 */
import { useRef, useState, type ReactNode } from 'react';
import type { NavActions } from '../../layouts/navPermissions';
import { BusyIcon } from '../loading';
import { ExportMenu, type ExportMenuHandle } from './ExportMenu';
import { LoadTimeChip } from '../LoadTimeChip';
import { AuditTrailButton } from '../AuditTrailButton';
import { useRegisterToolbar, useFormMeta } from '../FormMetaContext';
import { useHotkeys, formatCombo, effectiveCombo, type HotkeyDef } from '../hotkeys';
import './AppToolbar.css';

/** Default toolbar bindings (CLAUDE.md standing rule 14 reserved-key policy). */
const TB_KEY = {
  new: 'alt+n', edit: 'alt+e', editAlt: 'f2', delete: 'alt+d',
  preview: 'alt+p', refresh: 'alt+r', import: 'alt+i', export: 'alt+x',
} as const;

/** "Label (Ctrl+S)" — the shortcut hint every bound button shows in its tooltip. */
const tip = (label: string, combo: string) => `${label} (${formatCombo(effectiveCombo(combo))})`;

/** The shared "Loaded in X sec" + view-only Audit chips — rendered at the END of
 *  the action buttons (immediately after "Export to" when present, else after the
 *  last button).  Values are bridged from <FormTitle> via FormMetaContext, so
 *  every form gets this with no per-form change.  View only — no registration. */
function MetaChipsView({ hideAudit = false, hideLoad = false }:
                       { hideAudit?: boolean; hideLoad?: boolean }) {
  const { loadMs, loading, auditFormName, auditSourceId } = useFormMeta();
  if (loadMs == null && !loading && !auditFormName) return null;
  // hideLoad renders the audit chip ALONE (used by `auditFirst`, which moves
  // only the audit action to the front and leaves "Loaded in" at the end).
  return (
    <span className="app-toolbar__meta">
      {!hideLoad && <LoadTimeChip ms={loadMs ?? null} loading={loading} />}
      {auditFormName && !hideAudit && (
        <AuditTrailButton variant="chip" formName={auditFormName} sourceId={auditSourceId} />
      )}
    </span>
  );
}

/**
 * Standalone Loaded + Audit chips for a BESPOKE action row (a form that doesn't
 * render the shared <AppToolbar>, or one whose <AppToolbar> is a header bar and
 * so passes `showMeta={false}`).  Drop this at the END of the PRIMARY action row
 * (right after the last button / "Export to") so the chips sit in the same place
 * as every other form.  It registers toolbar presence itself (so <FormTitle>
 * defers its chips here), then renders them (BO UI Standard v3, user request
 * 2026-09-18: chips go after the last left-side action button, globally).
 */
export function FormMetaChips() {
  useRegisterToolbar();
  return <MetaChipsView />;
}

/** One option in the "Export to" dropdown (ALPACA-style multi-format export). */
export interface ExportOption { label: string; icon?: string; onSelect: () => void; }

/** Data for the shared full-format "Export to" (CSV / HTML / Image / MHT / Text /
 *  PDF / RTF / XLS / XLSX / DOCX).  Pass the form's FULL filtered dataset (built
 *  with the shared `gridExportMatrix(filtered, columns)`) so ALL rows export, not
 *  just the visible page.  Takes precedence over `exportOptions` / `onExport`. */
export interface ExportData {
  filename: string; headers: string[]; rows: string[][];
  /** Optional async source for ALL rows — used by server-paginated lists
   *  (ModuleListView) that must fetch every page for the export.  When set, the
   *  static `headers`/`rows` seed the visible-page fallback and this supplies the
   *  full dataset at export time. */
  rowsProvider?: () => Promise<{ headers: string[]; rows: string[][] }>;
  /** OPT-IN: G5's split "Export to" — the button exports CSV, its ▾ opens every
   *  format (ExportMenu `defaultCsv`). Unset = the plain menu button. */
  defaultCsv?: boolean;
}

export interface AppToolbarProps {
  /** Form-specific buttons rendered to the LEFT of New (e.g. Save, Save As,
   *  Query, Validate, Approve, Deliver).  Use shared `.ca-btn` buttons. */
  left?: ReactNode;

  /** New / Add. */
  onNew?: () => void;
  newLabel?: string;
  newDisabled?: boolean;
  /** Tooltip of the New button while it is DISABLED — why it is off (opt-in,
   *  owner 2026-10-19, PLM Daily QC, G5 parity). Enabled, it keeps "New (Alt+N)". */
  newDisabledTitle?: string;
  /** OPT-IN: the New button's TOOLTIP text when it differs from its caption — XAF
   *  names the object, e.g. "New Reagent Preparation" (G5 parity, 2026-11-03). The
   *  shortcut is still appended. Unset = the caption (unchanged). */
  newTitle?: string;
  /** OPT-IN: append XAF's selection hints to the Edit and Delete tooltips —
   *  "Requires a single object selected" / "Requires at least one object selected",
   *  as G5's list toolbar shows them (2026-11-03). Unset = unchanged. */
  selectionHints?: boolean;
  /** Render New BEFORE the `left` slot instead of after it (legacy-parity
   *  opt-in — see the header note).  Default false = canonical order. */
  newFirst?: boolean;

  /** Edit the selected row. */
  onEdit?: () => void;
  editDisabled?: boolean;
  /** Render Edit BEFORE the `left` slot instead of after it (legacy-parity
   *  opt-in — see the header note).  Default false = canonical order. */
  editFirst?: boolean;

  /** Delete the selected row(s). */
  onDelete?: () => void;
  deleteDisabled?: boolean;
  /** Caption of the Delete button (default "Delete") — e.g. "Remove" where the
   *  legacy bar says so. Glyph, position and Alt+D are unchanged. */
  deleteLabel?: string;
  /** Tooltip of the Delete button while it is DISABLED — why it is off (opt-in,
   *  2026-10-17, PLM Daily QC). Enabled, it keeps the standard "Delete (Alt+D)". */
  deleteDisabledTitle?: string;

  /** Render Refresh FIRST, before everything else on the bar (legacy-parity
   *  opt-in, 2026-10-14 — Contract Lab Test Pricing, whose G5 bar is
   *  Refresh · Add · Remove · Save · Export to · Search). Same button, glyph and
   *  Alt+R; only its position changes. Default false = canonical order. */
  refreshFirst?: boolean;

  /** Render Preview (and its `beforePreview` slot) AFTER Export to instead of
   *  before Refresh (opt-in, 2026-10-16 — PLM Contamination Check, whose
   *  prototype bar is Refresh · Export to · Preview). Same button, glyph and
   *  Alt+P; only its position changes. Default false = canonical order. */
  previewLast?: boolean;

  /** Render the whole New · Edit · Delete group BEFORE the `left` slot instead
   *  of around it (legacy-parity opt-in — see the header note).  Default false
   *  = canonical order.  Supersedes `newFirst` / `editFirst` when passed. */
  crudFirst?: boolean;

  /** Slot rendered immediately BEFORE the Preview button — for a control that
   *  Preview acts ON, such as a report picker, which reads backwards when it
   *  follows the action it feeds ("Preview · Report [x]"). Distinct from
   *  `beforeRefresh`, which sits AFTER Preview and is used elsewhere for
   *  unrelated extras (Control Chart's Guide button); keeping the two separate
   *  means adopting this does not shuffle any other form's toolbar. */
  beforePreview?: ReactNode;

  /** Preview the selected record (e.g. render its PDF).  Rendered after Delete,
   *  before Refresh.  Opt-in — only shows when provided. */
  onPreview?: () => void;
  previewDisabled?: boolean;
  previewLabel?: string;

  /** Reload data — ALWAYS labelled "Refresh". */
  onRefresh?: () => void;
  refreshing?: boolean;
  refreshDisabled?: boolean;

  /** Import (bulk CSV upload) — shown ONLY for lookups that support bulk load
   *  (BO UI Standard §6).  Rendered after Refresh, before Export. */
  /** Render Import + Template at the very START of the bar, before everything
   *  else, instead of their canonical place after Refresh (legacy-parity opt-in,
   *  owner decision 2026-10-13 for Test Code, whose ALPACA bar reads
   *  Audit Trail - Import File - Template Download - New - ...). Same shared
   *  buttons, same auto-registered Alt+I; only the position changes. */
  /** Caption for the Import button. Defaults to 'Import'; Test Code passes
   *  'Import File' to match its ALPACA bar. */
  importLabel?: string;
  /** Caption for the Template button. Defaults to 'Template'; Test Code passes
   *  'Template Download'. */
  templateLabel?: string;
  importFirst?: boolean;
  /** Render the view-only Audit chip at the very START of the bar instead of in
   *  the trailing meta cluster (same owner decision). The "Loaded in" chip stays
   *  where it is, so this moves ONLY the audit action. */
  auditFirst?: boolean;
  onImport?: () => void;
  importDisabled?: boolean;
  /** Template download (blank CSV of the columns) — paired with Import. */
  onTemplate?: () => void;

  /** Export to (CSV / XLSX / …) — the form owns the actual export/menu. */
  onExport?: () => void;
  exportLabel?: string;
  exportDisabled?: boolean;
  /** When given, "Export to" becomes a dropdown of these formats (ALPACA parity).
   *  Takes precedence over `onExport`. */
  exportOptions?: ExportOption[];
  /** SHARED full-format "Export to" (CSV / HTML / Image / MHT / Text / PDF / RTF /
   *  XLS / XLSX / DOCX) that exports ALL the rows you pass.  This is the standard
   *  export for every list form — build it with `gridExportMatrix(filtered, cols)`.
   *  Takes precedence over `exportOptions` and `onExport`. */
  exportData?: ExportData;

  /** Content rendered between Delete and Refresh (e.g. Date Filter on Sample
   *  Registration).  Separated by dividers automatically. */
  beforeRefresh?: ReactNode;

  /** Content rendered AFTER Refresh (and Import/Template) and BEFORE Export — for
   *  a form-specific action that belongs next to Export, e.g. a "Reports ▾"
   *  dropdown.  Opt-in; renders nothing when unset. */
  beforeExport?: ReactNode;

  /** Extra content rendered at the very RIGHT (rare — e.g. a load-time chip). */
  right?: ReactNode;

  /** Whether THIS toolbar renders the Loaded + Audit chips at its end (default
   *  true).  Set false for a HEADER bar (e.g. Back · workflow-action) when the
   *  form's PRIMARY actions live in a separate bespoke row — then render
   *  <FormMetaChips/> at the end of that primary row instead, so the chips sit
   *  after the last real action button (BO UI Standard v3). */
  showMeta?: boolean;

  /** Register NO keyboard shortcuts. For a SECOND toolbar on a page that already
   *  has one (e.g. a child grid's section bar under the form's main bar): the
   *  hotkey store is last-registered-wins, so without this the child bar would
   *  take Alt+N / Alt+D / Alt+R / Alt+X away from the page's primary toolbar.
   *  The buttons still work by click. Default false. */
  noHotkeys?: boolean;

  /** Opt-in ALPACA Module Access Control (MAC) gating.  When provided, the
   *  mutating buttons are ADDITIONALLY disabled unless the user holds the
   *  matching right — New needs Create, Edit needs Write, Delete needs Delete —
   *  so a Read-only user sees the form but every action greyed (Refresh / Export
   *  stay on, since viewing + exporting are reads).  Pass `useNavActions(formId)`:
   *  it is all-true for admins and for a form with no MAC rows, so this only ever
   *  TIGHTENS where the lab configured a MAC level, never strips a button a form
   *  shows today.  Additive with the explicit `*Disabled` props. */
  perm?: NavActions;
}

export function AppToolbar({
  left,
  onNew, newLabel = 'New', newDisabled, newDisabledTitle, newTitle, selectionHints = false, newFirst = false,
  onEdit, editDisabled, editFirst = false,
  crudFirst = false,
  onDelete, deleteDisabled, deleteLabel = 'Delete', deleteDisabledTitle,
  refreshFirst = false,
  previewLast = false,
  beforePreview,
  onPreview, previewDisabled, previewLabel = 'Preview',
  onRefresh, refreshing, refreshDisabled,
  onImport, importDisabled, onTemplate, importFirst = false, auditFirst = false,
  importLabel = 'Import File', templateLabel = 'Template Download',
  beforeRefresh,
  beforeExport,
  onExport, exportLabel = 'Export to', exportDisabled, exportOptions, exportData,
  right, showMeta = true, noHotkeys = false, perm,
}: AppToolbarProps) {
  // ALPACA MAC gating (opt-in): New needs Create, Edit needs Write, Delete needs
  // Delete.  Additive with the explicit *Disabled props; no `perm` = unchanged.
  const newDisabledEff    = newDisabled    || (perm ? !perm.create : false);
  const editDisabledEff   = editDisabled   || (perm ? !perm.write  : false);
  const deleteDisabledEff = deleteDisabled || (perm ? !perm.delete : false);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Ref to the shared multi-format ExportMenu so the Alt+X shortcut can open it.
  const exportMenuRef = useRef<ExportMenuHandle>(null);
  // Tell <FormTitle> a toolbar is present so it hands its load-time + Audit
  // chips to us (rendered next to "Export to" below) instead of the title row.
  useRegisterToolbar();

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): every form
  // that renders this toolbar inherits New/Edit/Delete/Preview/Refresh/Import/
  // Export keys with ZERO per-form code.  We register only the actions that are
  // PRESENT (handler supplied) and mirror their `disabled` state, so a binding is
  // inert + greyed in the ? sheet exactly when its button is.  Alt+X covers ALL
  // export variants — it OPENS the multi-format "Export to ▾" dropdown
  // (exportData) or the custom exportOptions menu, or runs the simple onExport.
  const hotkeys: HotkeyDef[] = [];
  if (onNew) hotkeys.push({ combo: TB_KEY.new, label: newLabel, group: 'Toolbar', order: 1, disabled: newDisabledEff, run: () => onNew() });
  if (onEdit) {
    hotkeys.push({ combo: TB_KEY.edit, label: 'Edit', group: 'Toolbar', order: 2, disabled: editDisabledEff, run: () => onEdit() });
    hotkeys.push({ combo: TB_KEY.editAlt, label: 'Edit', group: 'Toolbar', order: 2, disabled: editDisabledEff, hidden: true, run: () => onEdit() });
  }
  if (onDelete) hotkeys.push({ combo: TB_KEY.delete, label: deleteLabel, group: 'Toolbar', order: 3, disabled: deleteDisabledEff, run: () => onDelete() });
  if (onPreview) hotkeys.push({ combo: TB_KEY.preview, label: previewLabel, group: 'Toolbar', order: 4, disabled: previewDisabled, run: () => onPreview() });
  if (onRefresh) hotkeys.push({ combo: TB_KEY.refresh, label: 'Refresh', group: 'Toolbar', order: 5, disabled: refreshDisabled || refreshing, run: () => onRefresh() });
  if (onImport) hotkeys.push({ combo: TB_KEY.import, label: importLabel, group: 'Toolbar', order: 6, disabled: importDisabled, run: () => onImport() });
  // Export Alt+X — opens the multi-format dropdown (exportData) or the custom
  // exportOptions menu, else runs the simple onExport.  The exportData menu is
  // always enabled (LIMS parity: "Export to" stays clickable even on an empty
  // grid); the other two honour exportDisabled.
  if (exportData || (exportOptions && exportOptions.length) || onExport) {
    hotkeys.push({
      combo: TB_KEY.export, label: exportLabel, group: 'Toolbar', order: 7,
      disabled: exportData ? false : exportDisabled,
      run: () => {
        if (exportData) exportMenuRef.current?.open();
        else if (exportOptions && exportOptions.length) setExportMenuOpen(true);
        else onExport?.();
      },
    });
  }
  useHotkeys(noHotkeys ? [] : hotkeys);

  /* Button glyphs are the EXACT BO UI Standard prototype characters
     (docs/prototypes/Settings-BO-Method-Example.html .tbtn .i spans).
     New = NEUTRAL button with a GREEN ＋ (BO UI Standard §7).  Orange is
     reserved for the ONE primary/active action (Save) — a solid-orange New
     would put two orange buttons on a detail bar (New + Save).
     Hoisted so `newFirst` can place it either side of the `left` slot without
     duplicating the markup. */
  // Import + Template as one fragment so `importFirst` can render them at the
  // start without duplicating the markup or losing the Alt+I registration.
  const importTemplateButtons = (
    <>
      {onImport && (
        <button className="ca-btn" onClick={onImport} disabled={importDisabled} title={tip(importLabel, TB_KEY.import)}>
          <span className="tb-i tb-i-green">📥</span> {importLabel}
        </button>
      )}
      {onTemplate && (
        <button className="ca-btn" onClick={onTemplate} title={templateLabel}>
          <span className="tb-i tb-i-grey">⬇</span> {templateLabel}
        </button>
      )}
    </>
  );

  const newButton = onNew ? (
    <button className="ca-btn" onClick={onNew} disabled={newDisabledEff}
      title={newDisabledEff && newDisabledTitle ? newDisabledTitle : tip(newTitle ?? newLabel, TB_KEY.new)}>
      <span className="tb-i tb-i-green">＋</span> {newLabel}
    </button>
  ) : null;

  /* Hoisted for the same reason as `newButton`: `editFirst` places it either
     side of the `left` slot without duplicating the markup, so the button, its
     glyph, its tooltip and its Alt+E / F2 registration stay identical. */
  const editButton = onEdit ? (
    <button className="ca-btn" onClick={onEdit} disabled={editDisabledEff} title={tip('Edit', TB_KEY.edit) + (selectionHints ? '\nRequires a single object selected' : '')}>
      <span className="tb-i tb-i-blue">✎</span> Edit
    </button>
  ) : null;

  /* Hoisted for the same reason as the two above, so `crudFirst` can put Delete
     with its group ahead of the `left` slot. */
  const deleteButton = onDelete ? (
    <button className="ca-btn" onClick={onDelete} disabled={deleteDisabledEff}
      title={deleteDisabledEff && deleteDisabledTitle ? deleteDisabledTitle : tip(deleteLabel, TB_KEY.delete) + (selectionHints ? '\nRequires at least one object selected' : '')}>
      <span className="tb-i tb-i-red">🗑</span> {deleteLabel}
    </button>
  ) : null;

  /* `crudFirst` is the group form of newFirst/editFirst, so it subsumes them --
     a form passing it gets New · Edit · Delete before `left` whatever the other
     two say, rather than the three flags fighting over the same positions. */
  const newIsFirst  = crudFirst || newFirst;
  const editIsFirst = crudFirst || editFirst;

  /* Hoisted so `previewLast` can put it after Export to. */
  const previewGroup = onPreview || beforePreview ? (
    <>
      {beforePreview}
      {onPreview && (
        <button className="ca-btn" onClick={onPreview} disabled={previewDisabled} title={tip(previewLabel, TB_KEY.preview)}>
          <span className="tb-i tb-i-blue">👁</span> {previewLabel}
        </button>
      )}
    </>
  ) : null;

  /* Hoisted so `refreshFirst` can put it at the very front of the bar. */
  const refreshButton = onRefresh ? (
    <button className="ca-btn" onClick={onRefresh} disabled={refreshDisabled || refreshing} title={tip('Refresh', TB_KEY.refresh)}>
      {refreshing ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
    </button>
  ) : null;

  return (
    <div className="dp-title-actions app-toolbar">
      {refreshFirst && refreshButton}
      {/* New and Edit sit after the form-specific `left` slot (canonical) unless
          the form opted into `newFirst` / `editFirst` for legacy parity — see
          the header note. */}
      {/* crudFirst keeps the group in canonical New · Edit · Delete order and
          simply moves the whole group ahead of `left`; editFirst alone still
          puts Edit first, as Vendors COA's legacy bar draws it. */}
      {auditFirst && <MetaChipsView hideLoad />}
      {importFirst && importTemplateButtons}
      {crudFirst ? newButton : editFirst && editButton}
      {crudFirst ? editButton : newFirst && newButton}
      {crudFirst && deleteButton}
      {left}
      {!newIsFirst && newButton}
      {!editIsFirst && editButton}
      {!crudFirst && deleteButton}
      {!previewLast && previewGroup}
      {beforeRefresh}
      {!refreshFirst && refreshButton}
      {!importFirst && importTemplateButtons}
      {beforeExport}
      {exportData ? (
        /* SHARED full-format Export to (all 10 ALPACA formats, ALL rows). */
        <ExportMenu ref={exportMenuRef} filename={exportData.filename} headers={exportData.headers} rows={exportData.rows}
                    rowsProvider={exportData.rowsProvider} triggerClassName="ca-btn" title={tip(exportLabel, TB_KEY.export)}
                    defaultCsv={exportData.defaultCsv} />
      ) : exportOptions && exportOptions.length > 0 ? (
        <span className="app-toolbar__export">
          <button className="ca-btn" onClick={() => setExportMenuOpen(o => !o)} disabled={exportDisabled} title={tip(exportLabel, TB_KEY.export)}>
            <span className="tb-i tb-i-teal">📤</span> {exportLabel} ▾
          </button>
          {exportMenuOpen && (
            <>
              <div className="app-toolbar__export-backdrop" onClick={() => setExportMenuOpen(false)} />
              <div className="app-toolbar__export-menu" role="menu">
                {exportOptions.map(opt => (
                  <button key={opt.label} className="app-toolbar__export-item" role="menuitem"
                          onClick={() => { setExportMenuOpen(false); opt.onSelect(); }}>
                    <i className={`ti ${opt.icon ?? 'ti-file-export'}`} /> {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </span>
      ) : onExport && (
        <button className="ca-btn" onClick={onExport} disabled={exportDisabled} title={tip(exportLabel, TB_KEY.export)}>
          <span className="tb-i tb-i-teal">📤</span> {exportLabel}
        </button>
      )}
      {previewLast && previewGroup}
      {right}
      {/* Load-time + Audit chips, right after "Export to" — bridged from
          <FormTitle> via FormMetaContext so every form shows them here (v3,
          2026-09-18).  A header bar with its primary actions elsewhere passes
          showMeta={false} and renders <FormMetaChips/> in that primary row. */}
      {showMeta && <MetaChipsView hideAudit={auditFirst} />}
    </div>
  );
}

/**
 * Thin vertical separator for grouping toolbar sections.  Drop between logical
 * button groups inside the `left` slot (e.g. Report · Save · actions · export).
 * Colour comes from the shared `--ai-toolbar-divider` theme token so theming
 * recolours it automatically — never hand-roll a per-form divider.
 */
export function ToolbarDivider() {
  return <span className="app-toolbar__divider" aria-hidden="true" />;
}
